import Export from "@/components/Export";
import SearchBox from "@/components/SearchBox";
import {
  useAllProjects,
  useCreateProject,
  useProjects,
  useUpdateProject,
} from "@/hooks/useProjects";
import { IProject } from "@/interfaces/project.interface";
import { RootState } from "@/store/store";
import { buildFilters, getPaginationData } from "@/utils/kendo";
import ToastService from "@/utils/toast";
import { Button } from "@progress/kendo-react-buttons";
import { useSearchFilter } from "@/hooks/useSearchFilters";
//@ts-ignore
import { PagerTargetEvent } from "@progress/kendo-react-data-tools/pager/Pager";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import {
  GridColumn as Column,
  Grid,
  GridDataStateChangeEvent,
  GridPageChangeEvent,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { Switch, TextBoxChangeEvent } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { Plus } from "lucide-react";
import { createRef, useEffect, useMemo, useState } from "react";
import Filters from "../../constants/filters";
import IPageState from "../../interfaces/page.interface";
import { useAppSelector } from "../../store/hooks/hooks";
import WriteDrawer from "./WriteDrawer";
import { columnInterface, generateColumns } from "./columns";
import { ComboBoxChangeEvent } from "@progress/kendo-react-dropdowns";
import { hideLoader, showLoader } from "@/store/slices/loaderSlice";
import { userSvc } from "@/services/user.service";
import { IUser } from "@/interfaces/user.interface";
import { useDispatch } from "react-redux";

let mappingChanged = false;

export const Listing = () => {
  const { selectedProject } = useAppSelector(
    (state: RootState) => state.projects
  );

  const dispatch = useDispatch();

  const _exporter = createRef<ExcelExport>();
  const Pagination = getPaginationData();

  const createProjectMutation = useCreateProject();
  const updateProjectMutation = useUpdateProject();

  const [addNewProject, setAddNewProject] = useState<
    (IProject & { id?: string }) | null
  >(null);
  const [columnsMapping, setColumnsMapping] = useState<any>(null);
  const [page, setPage] = useState<IPageState>(Pagination.initialDataState);
  const [pageSizeValue, setPageSizeValue] = useState<
    number | string | undefined
  >();
  const [showDrawer, setShowDrawer] = useState(false);
  const [projects, setProjects] = useState<IProject[]>([]);
  const [users, setUsers] = useState<IUser[]>([]);
  const [stateColumns, setStateColumns] = useState<any>(
    generateColumns(handleEditClick, onColumnsSubmit, null, enableDisableProj)
  );
  const [showArchived, setShowArchived] = useState(false);

  const { searchValue, dataState, handleSearchChange, setDataState } =
    useSearchFilter({
      initialState: Pagination.initialDataState,
      searchFields: ["name"],
    });

  const filterParams = useMemo(
    () => ({
      pageNumber: page.skip,
      pageSize: page.take,
      sort: dataState.sort,
      filter: buildFilters("", dataState.filter, showArchived),
    }),
    [page.skip, page.take, dataState.sort, dataState.filter, showArchived]
  );

  const { data: projectsData, refetch } = useProjects(filterParams);

  const { data: allProjectsData, refetch: refetchAllProjects } = useAllProjects(
    { filter: buildFilters(selectedProject?.id) },
    { enabled: false }
  );

  const excelExport = () => {
    if (_exporter.current) {
      if (projectsData?.data && projectsData.data.length > 0) {
        // Get the formatted data

        // Save with the formatted data
        _exporter.current.save(projectsData?.data);
      } else {
        refetch().then(() => {
          setTimeout(() => {
            _exporter.current?.save(projectsData?.data || []);
          }, 300);
        });
      }
    }
  };

  function onColumnsSubmit(columnsState: Array<columnInterface>) {
    const columnsMapping: any = [];
    for (let col of columnsState) {
      if (col.show) {
        columnsMapping.push(col.field);
      }
    }
    setColumnsMapping(columnsMapping);
    mappingChanged = true;
    const cols = generateColumns(
      handleEditClick,
      onColumnsSubmit,
      columnsState,
      enableDisableProj
    );
    setStateColumns(cols);
  }

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? projects.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const handleChange = (e: TextBoxChangeEvent | ComboBoxChangeEvent) => {
    if (e.target.name === "constructionManager") {
      setAddNewProject((prev: any) => {
        return {
          ...prev,
          [e.target.name as any]: e.value?.id,
        };
      });
      return;
    }

    setAddNewProject((prev: any) => {
      return { ...prev, [e.target.name as any]: e.target.value };
    });
  };

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
  };

  const createProject = () => {
    if (!addNewProject) return;

    createProjectMutation.mutate(
      { ...addNewProject, isActive: true },
      {
        onSuccess: (data) => {
          ToastService.success("Project created successfully");
          setShowDrawer(false);
        },
      }
    );
  };

  const editProject = async () => {
    if (!addNewProject?.id) return;

    updateProjectMutation.mutate(
      { id: addNewProject.id, data: addNewProject },
      {
        onSuccess: () => {
          ToastService.success("Project updated successfully");
          setShowDrawer(false);
        },
      }
    );
  };

  function enableDisableProj(dataItem: IProject) {
    if (!dataItem?.id) return;
    updateProjectMutation.mutate(
      { id: dataItem.id, data: { ...dataItem, isActive: !dataItem.isActive } },
      {
        onSuccess: () => {
          ToastService.success("Project updated successfully");
        },
      }
    );
  }

  function handleEditClick(data: IProject) {
    setAddNewProject(data);
    setShowDrawer(true);
  }

  useEffect(() => {
    if (!showDrawer) {
      setAddNewProject(null);
    } else {
      fetchUsers();
    }
  }, [showDrawer]);

  const fetchUsers = async () => {
    try {
      dispatch(showLoader());
      await userSvc.getAll().then((res) => {
        if (res.error) {
          throw new Error(res.error.message);
        }
        setUsers(res.data as IUser[]);
      });
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(hideLoader());
    }
  };

  useEffect(() => {
    if (projectsData?.data) {
      setProjects(projectsData.data);
    }
  }, [projectsData]);

  useEffect(() => {
    if (!projects?.length || !columnsMapping) return;
    const cols = generateColumns(
      handleEditClick,
      onColumnsSubmit,
      null,
      enableDisableProj
    );
    setStateColumns(cols);
  }, [projects, columnsMapping]);

  return (
    <>
      <div className="setting-page">
        <Grid
          style={{ maxHeight: "" }}
          data={projects}
          {...dataState}
          onDataStateChange={dataStateChange}
          skip={page.skip}
          take={page.take}
          total={projectsData?.total || 0}
          pageable={{
            ...Pagination.pageSizeValue,
            pageSizeValue: pageSizeValue,
          }}
          onPageChange={pageChange}
          className={`customHeight employee`}
          resizable={Filters.resizeable}
          selectable={{
            enabled: true,
            drag: true,
            mode: "multiple",
          }}
        >
          <GridToolbar>
            <div className="flex gap-3 w-full items-center">
              <div className="w-[250px]">
                <SearchBox
                  value={searchValue}
                  onChange={handleSearchChange}
                  placeholder="Search"
                />
              </div>
              <Button
                themeColor={"primary"}
                className="border border-[#E2E8F0]"
                onClick={() => setShowDrawer(true)}
              >
                <span className="inline-flex items-center gap-2 font-normal">
                  <Plus className="w-4 h-4" /> Add New
                </span>
              </Button>
              <div className="flex items-center gap-2 md:order-3 sm:order-2 order-4 sm:mt-0 mt-2 sm:ml-0 ml-auto">
                <Label className="font-medium text-sm font-cabin">
                  Show Inactive
                </Label>
                <Switch
                  defaultChecked={false}
                  onChange={(e) => {
                    setShowArchived(e.value);
                  }}
                />
              </div>
              <div
                className="cursor-pointer sm:ml-0 ml-auto sm:flex-initial flex-1"
                onClick={excelExport}
              >
                <Export />
                <ExcelExport
                  data={allProjectsData?.data || []}
                  collapsible={true}
                  fileName="Projects.xlsx"
                  ref={_exporter}
                >
                  {stateColumns &&
                    stateColumns.length > 0 &&
                    stateColumns.map((column: any, idx: number) => {
                      if (column.show) {
                        return (
                          <ExcelExportColumn
                            key={idx}
                            field={column.field}
                            title={column.title}
                          />
                        );
                      }
                    })}
                </ExcelExport>
              </div>
            </div>
          </GridToolbar>
          {stateColumns &&
            stateColumns.length > 0 &&
            stateColumns.map((column: any, idx: number) => {
              if (column.show) {
                if (column.cells) {
                  return (
                    <Column
                      key={idx}
                      field={column.field}
                      title={column.title}
                      columnMenu={column.columnMenu}
                      minResizableWidth={200}
                      cells={column.cells}
                      filter={column.filter ? column.filter : "text"}
                    />
                  );
                }
                return (
                  <Column
                    key={idx}
                    field={column.field}
                    title={column.title}
                    columnMenu={column.columnMenu}
                    minResizableWidth={200}
                    filter={column.filter ? column.filter : "text"}
                  />
                );
              }
            })}
        </Grid>
      </div>
      <WriteDrawer
        showCreateDrawer={showDrawer}
        setShowCreateDrawer={setShowDrawer}
        addNewProject={addNewProject}
        handleChange={handleChange}
        onSave={addNewProject?.id ? editProject : createProject}
        users={users}
      />
    </>
  );
};
